// ==============================================================================
// VERSION INFORMATION
// ==============================================================================
// __version__ = "1.0.0"
//
// Changelog:
//
// Version 1.0.0:
// - [Added] Initial implementation of a simple global event emitter (Pub/Sub)
//   to facilitate real-time communication between disconnected components.
// ==============================================================================

type EventHandler = (data?: any) => void;

class EventEmitter {
  private events: Record<string, EventHandler[]>;

  constructor() {
    this.events = {};
  }

  /**
   * Subscribes a handler to a specific event.
   * @param eventName The name of the event to listen for.
   * @param handler The callback function to execute when the event is emitted.
   */
  on(eventName: string, handler: EventHandler): void {
    if (!this.events[eventName]) {
      this.events[eventName] = [];
    }
    this.events[eventName].push(handler);
  }

  /**
   * Unsubscribes a handler from a specific event.
   * @param eventName The name of the event.
   * @param handler The callback function to remove.
   */
  off(eventName: string, handler: EventHandler): void {
    if (!this.events[eventName]) {
      return;
    }
    this.events[eventName] = this.events[eventName].filter(h => h !== handler);
  }

  /**
   * Emits an event, calling all subscribed handlers with the provided data.
   * @param eventName The name of the event to emit.
   * @param data The data to pass to the event handlers.
   */
  emit(eventName: string, data?: any): void {
    if (!this.events[eventName]) {
      return;
    }
    this.events[eventName].forEach(handler => handler(data));
  }
}

// Create and export a singleton instance to be used throughout the application.
const eventEmitter = new EventEmitter();
export default eventEmitter;